import { READING_SCALE, parseReading, scaledToDecimal } from './moisture-determination.js';
/**
 * EXACT DECIMAL HELPERS for repeating determinations (D260).
 *
 * The arithmetic the ASTM D2216 component introduced (D241), made available
 * to later templates without a formula engine: readings are decimal TEXT held
 * as scaled integers (`READING_SCALE` places), sums and differences are exact,
 * and a division is carried to a fixed number of places, rounded half away
 * from zero at the last place. That is storage precision, not a rounding rule:
 * what a person sees is shaped by `formatDecimalForDisplay` with each
 * template's own workbook number format.
 *
 * Each template still writes its own formula, traced to its own workbook cell.
 */
/** numerator / denominator, rounded half away from zero to an integer. */
export function divideHalfAwayFromZero(numerator, denominator) {
    if (denominator === 0n)
        throw new RangeError('Division by zero.');
    const negative = numerator < 0n !== denominator < 0n;
    const n = numerator < 0n ? -numerator : numerator;
    const d = denominator < 0n ? -denominator : denominator;
    const quotient = (2n * n + d) / (2n * d);
    return negative ? -quotient : quotient;
}
/**
 * The ratio of two values that share one scale (the scales cancel), as
 * decimal text carried to `places`. The caller has refused a denominator of
 * zero or below before asking.
 */
export function ratioToDecimal(numerator, denominator, places) {
    return scaledToDecimal(divideHalfAwayFromZero(numerator * 10n ** BigInt(places), denominator), places);
}
/** Decimal text as an integer scaled by 10^places, or null when it is not one. */
export function decimalToScaled(text, places) {
    if (text === null || text === undefined)
        return null;
    const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(text.trim());
    if (!match)
        return null;
    const [, sign, whole, fraction = ''] = match;
    if (fraction.length > places)
        return null;
    const scaled = BigInt(`${whole}${fraction.padEnd(places, '0')}`);
    return sign === '-' ? -scaled : scaled;
}
/**
 * The arithmetic mean of decimal values carried to `places`, or null when
 * there are none. Each value must already be carried to `places` or fewer.
 */
export function meanOfDecimals(values, places) {
    if (values.length === 0)
        return null;
    let sum = 0n;
    for (const value of values) {
        const scaled = decimalToScaled(value, places);
        if (scaled === null)
            return null;
        sum += scaled;
    }
    return scaledToDecimal(divideHalfAwayFromZero(sum, BigInt(values.length)), places);
}
/** Why one reading of a row cannot be used. */
export const READING_ISSUE_CODE = Object.freeze({
    /** A reading is missing. Never treated as zero (CLAUDE.md section 8). */
    READING_REQUIRED: 'reading_required',
    /** Not a plain decimal number of the accepted size. */
    NOT_A_DECIMAL: 'not_a_decimal',
    /** A mass below zero. */
    NEGATIVE_MASS: 'negative_mass',
});
/**
 * Read the mass readings of one row of a repeating determination: each
 * missing, not a number, negative, or a usable scaled value. The row's own
 * relationships (a denominator above zero) are the template's to check.
 */
export function readMassRow(fields, row) {
    const values = new Map();
    const issues = [];
    let missing = false;
    for (const field of fields) {
        const parsed = parseReading(row[field]);
        if (parsed.kind === 'missing') {
            missing = true;
            issues.push({ field, code: READING_ISSUE_CODE.READING_REQUIRED });
        }
        else if (parsed.kind === 'invalid') {
            issues.push({ field, code: READING_ISSUE_CODE.NOT_A_DECIMAL });
        }
        else if (parsed.scaled < 0n) {
            issues.push({ field, code: READING_ISSUE_CODE.NEGATIVE_MASS });
        }
        else {
            values.set(field, parsed.scaled);
        }
    }
    return { values, issues, missing };
}
/** A reading's scaled value back as plain decimal text. */
export function readingToDecimal(scaled) {
    return scaledToDecimal(scaled, READING_SCALE);
}
//# sourceMappingURL=exact-decimal.js.map